import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { accessibleDivisionNames } from "../lib/access";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PriorityBadge, HandoffStatusBadge, OverdueBadge } from "../components/PriorityBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  FileStack,
  ListTodo,
  Wrench,
  BellRing,
  ArrowRight,
  PlusCircle,
  Clock,
  CalendarClock,
  TriangleAlert,
} from "lucide-react";
import { fmtTime, todayKey, zonedDateKey } from "../lib/timezone";
import { StatusBadge } from "../components/AdvisoryBits";

const relDay = (key, tz) => {
  const t = todayKey(tz);
  if (key === t) return "Today";
  const [y, m, d] = t.split("-").map(Number);
  const tm = new Date(y, m - 1, d + 1);
  const tmKey = `${tm.getFullYear()}-${String(tm.getMonth() + 1).padStart(2, "0")}-${String(tm.getDate()).padStart(2, "0")}`;
  if (key === tmKey) return "Tomorrow";
  const [ky, km, kd] = key.split("-").map(Number);
  return new Date(ky, km - 1, kd).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

const fmt = (iso, tz) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: tz || "America/Chicago",
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

function Stat({ icon: Icon, label, value, tint, testid }) {
  return (
    <Card data-testid={testid} className="flex items-center gap-4 rounded-md border border-slate-200 bg-white p-5 shadow-sm">
      <div className={`flex h-11 w-11 items-center justify-center rounded-md ${tint}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-black text-slate-900 font-display leading-none">{value}</p>
        <p className="mt-1 text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
      </div>
    </Card>
  );
}

function HandoffRow({ h, tz }) {
  return (
    <Link
      to={`/handoffs/${h.id}`}
      data-testid={`handoff-row-${h.id}`}
      className="group flex items-center justify-between gap-4 border-b border-slate-100 px-1 py-3 last:border-0 transition-colors hover:bg-slate-50"
    >
      <div className="min-w-0">
        <p className="truncate font-display font-bold text-slate-900" data-testid={`handoff-title-${h.id}`}>{h.title}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <PriorityBadge priority={h.priority} />
          <HandoffStatusBadge status={h.status} />
          {h.overdue && <OverdueBadge />}
          {!h.acknowledged && (
            <span className="rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-blue-600">New</span>
          )}
        </div>
        <p className="mt-1 truncate text-sm text-slate-500">{h.summary}</p>
        <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
          <Clock className="h-3 w-3" /> From: {h.shift_name} · {h.created_by_name} · {fmt(h.date_time, tz)}
        </p>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-blue-600" />
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [handoffs, setHandoffs] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [f, setF] = useState({ status: "all", shift: "all", priority: "all", division: "all" });
  const [nextShift, setNextShift] = useState(undefined);
  const [adv, setAdv] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/advisories/summary").then((r) => setAdv(r.data)).catch(() => setAdv({ unresolved: 0, needs_verification: 0, recent_changes: [] }));
    api.get("/handoffs").then((r) => setHandoffs(r.data)).catch(() => {});
    api.get("/shifts").then((r) => setShifts(r.data)).catch(() => {});
    api.get("/divisions").then((r) => setDivisions(r.data)).catch(() => {});
    api.get("/schedule/mine").then((r) => {
      const now = Date.now();
      const up = [...(r.data.assignments || [])]
        .filter((a) => (a.end ? new Date(a.end).getTime() >= now : true))
        .sort((x, y) => (x.start || "").localeCompare(y.start || ""));
      setNextShift(up[0] || null);
    }).catch(() => setNextShift(null));
  }, []);

  const setFilter = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  if (!data) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-md bg-slate-200/70" />
        ))}
      </div>
    );
  }

  const s = data.stats;

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Operations Overview</p>
          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
        </div>
        <Link to="/handoffs/new">
          <Button data-testid="dashboard-create-handoff" className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
            <PlusCircle className="h-4 w-4" /> New Handoff
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testid="stat-handoffs" icon={FileStack} label="Total Handoffs" value={s.total_handoffs} tint="bg-blue-50 text-blue-600" />
        <Stat testid="stat-open-tasks" icon={ListTodo} label="Open Tasks" value={s.open_tasks} tint="bg-indigo-50 text-indigo-600" />
        <Stat testid="stat-equipment" icon={Wrench} label="Equipment Issues" value={s.equipment_issues} tint="bg-orange-50 text-orange-600" />
        <Stat testid="stat-unacked" icon={BellRing} label="Unacknowledged" value={s.unacknowledged} tint="bg-red-50 text-red-600" />
      </div>

      {/* Compact personal Next Shift widget */}
      <Card data-testid="dashboard-next-shift" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white">
            <CalendarClock className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Next Shift</p>
            {nextShift === undefined ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : nextShift ? (
              <p className="text-sm text-slate-700">
                <span className="font-semibold text-slate-900">{relDay(zonedDateKey(nextShift.start, user?.org_timezone), user?.org_timezone)}</span>
                {" · "}{fmtTime(nextShift.start, user?.org_timezone)} – {fmtTime(nextShift.end, user?.org_timezone)}
                {" · "}<span className="font-semibold">{nextShift.shift_name}</span>
                {nextShift.schedule_role ? <span className="text-slate-500"> · {nextShift.schedule_role}</span> : null}
              </p>
            ) : (
              <p className="text-sm text-slate-400" data-testid="dashboard-no-next-shift">No upcoming shifts scheduled</p>
            )}
          </div>
        </div>
        <Link to="/schedule?me=1">
          <Button data-testid="view-my-schedule-btn" variant="outline" size="sm" className="gap-1 text-blue-700 border-blue-200 hover:bg-blue-50">
            View My Schedule <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </Link>
      </Card>

      {/* Operational Advisories */}
      <Card data-testid="dashboard-advisories" className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
            <TriangleAlert className="h-4 w-4 text-amber-600" /> Operational Advisories
          </h2>
          <div className="flex items-center gap-2">
            <span data-testid="dashboard-adv-unresolved" className="rounded-md bg-blue-50 px-2 py-1 text-xs font-bold text-blue-700">{adv?.unresolved ?? "—"} unresolved</span>
            <span data-testid="dashboard-adv-needs-verification" className={`rounded-md px-2 py-1 text-xs font-bold ${adv?.needs_verification ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{adv?.needs_verification ?? "—"} need verification</span>
            <Link to="/advisories">
              <Button data-testid="dashboard-view-advisories" variant="outline" size="sm" className="gap-1 border-blue-200 text-blue-700 hover:bg-blue-50">View all <ArrowRight className="h-3.5 w-3.5" /></Button>
            </Link>
          </div>
        </div>
        <div className="mt-3 space-y-1.5" data-testid="dashboard-adv-recent">
          {!adv ? <p className="text-sm text-slate-400">Loading…</p> : adv.recent_changes.length === 0 ? (
            <p className="text-sm text-slate-400">No new or changed advisories in the last 24 hours.</p>
          ) : adv.recent_changes.map((a) => (
            <Link key={a.id} to={`/advisories/${a.id}`} data-testid={`dashboard-adv-${a.id}`} className="flex items-center justify-between gap-3 rounded-md border border-slate-100 px-3 py-2 transition-colors hover:bg-slate-50">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">{a.title}</p>
                <p className="truncate text-xs text-slate-500">{a.category} · {a.location_description}</p>
              </div>
              <StatusBadge status={a.effective_status} />
            </Link>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
          <h2 className="font-display text-lg font-bold text-slate-900">Latest Shift Handoffs</h2>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap" data-testid="dashboard-filters">
            <Select value={f.status} onValueChange={setFilter("status")}>
              <SelectTrigger data-testid="filter-status" className="h-9 w-full text-xs sm:w-36"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={f.shift} onValueChange={setFilter("shift")}>
              <SelectTrigger data-testid="filter-shift" className="h-9 w-full text-xs sm:w-36"><SelectValue placeholder="Shift" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All shifts</SelectItem>
                {shifts.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={f.priority} onValueChange={setFilter("priority")}>
              <SelectTrigger data-testid="filter-priority" className="h-9 w-full text-xs sm:w-32"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="important">Important</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            <Select value={f.division} onValueChange={setFilter("division")}>
              <SelectTrigger data-testid="filter-division" className="h-9 w-full text-xs sm:w-40"><SelectValue placeholder="Division" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All divisions</SelectItem>
                {accessibleDivisionNames(user, divisions.filter((d) => d.active !== false)).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4" data-testid="latest-handoffs-list">
            {(() => {
              const list = handoffs.filter((h) => {
                if (f.status !== "all" && h.status !== f.status) return false;
                if (f.shift !== "all") {
                  const rs = h.recipient_shifts || [];
                  const matches = rs.includes("__all__") || rs.includes(f.shift);
                  if (!matches) return false;
                }
                if (f.priority !== "all" && h.priority !== f.priority) return false;
                if (f.division !== "all" && (h.division || "") !== f.division) return false;
                return true;
              });
              return list.length === 0 ? (
                <p className="py-6 text-sm text-slate-400">No handoffs match these filters.</p>
              ) : (
                list.slice(0, 12).map((h) => <HandoffRow key={h.id} h={h} tz={user?.org_timezone} />)
              );
            })()}
          </div>
        </Card>

        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="font-display text-lg font-bold text-slate-900">Needs Your Acknowledgement</h2>
          <div className="mt-3 space-y-2" data-testid="unacknowledged-list">
            {data.unacknowledged.length === 0 ? (
              <p className="py-6 text-sm text-slate-400">You're all caught up.</p>
            ) : (
              data.unacknowledged.map((h) => (
                <Link
                  key={h.id}
                  to={`/handoffs/${h.id}`}
                  data-testid={`unacked-${h.id}`}
                  className="block rounded-md border border-slate-200 p-3 transition-colors hover:border-blue-300 hover:bg-blue-50/40"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-slate-900" data-testid={`unacked-title-${h.id}`}>{h.title}</span>
                    <PriorityBadge priority={h.priority} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">From: {h.shift_name} · {fmt(h.date_time, user?.org_timezone)}</p>
                </Link>
              ))
            )}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
            <BellRing className="h-4 w-4 text-red-600" /> High-Priority Updates
          </h2>
          <div className="mt-3 space-y-2" data-testid="high-priority-list">
            {data.high_priority.length === 0 ? (
              <p className="py-4 text-sm text-slate-400">Nothing flagged.</p>
            ) : (
              data.high_priority.map((h) => (
                <Link key={h.id} to={`/handoffs/${h.id}`} className="block rounded-md border border-slate-100 p-3 transition-colors hover:bg-slate-50">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-900" data-testid={`high-priority-title-${h.id}`}>{h.title}</span>
                    <PriorityBadge priority={h.priority} />
                  </div>
                  <p className="mt-1 truncate text-xs text-slate-500">{h.summary}</p>
                </Link>
              ))
            )}
          </div>
        </Card>

        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
            <ListTodo className="h-4 w-4 text-indigo-600" /> Open Tasks
          </h2>
          <div className="mt-3 space-y-2" data-testid="open-tasks-list">
            {data.open_tasks.length === 0 ? (
              <p className="py-4 text-sm text-slate-400">No open tasks.</p>
            ) : (
              data.open_tasks.slice(0, 6).map((t) => (
                <div key={t.id} className="flex items-start gap-2 rounded-md border border-slate-100 p-2.5">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                  <p className="text-sm text-slate-700">{t.title}</p>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
            <Wrench className="h-4 w-4 text-orange-600" /> Equipment Issues
          </h2>
          <div className="mt-3 space-y-2" data-testid="equipment-list">
            {data.equipment_issues.length === 0 ? (
              <p className="py-4 text-sm text-slate-400">No reported issues.</p>
            ) : (
              data.equipment_issues.map((h) => (
                <Link key={h.id} to={`/handoffs/${h.id}`} className="block rounded-md border border-orange-100 bg-orange-50/40 p-3 transition-colors hover:bg-orange-50">
                  <p className="truncate text-sm font-semibold text-slate-900">{h.title}</p>
                  <p className="mt-1 text-sm text-slate-700">{h.equipment_issues}</p>
                  <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-orange-600">{h.shift_name}</p>
                </Link>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
